import { Grid } from "@mui/material";
import React, { useMemo, useEffect } from "react";
import { AppCustomeSelect, CircularLoader } from "../Common";
import { AppSingleFieldRangeDatePicker } from "../Common/DateRangePicker";
import { StyledWorkScheduleListConstructionSite } from "./styles";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { workScheduleApi } from "@api";
import { useDispatch } from "react-redux";
import { updateSnackBar, useAppSelector, selectAuth } from "@redux";
import { useConstructionSites } from "@utils";
import { noop } from "lodash";
import moment from "moment";
import { useUserSelectionHistories } from "src/Utils/Hooks/useUserSelectionHistories";
import { useConstructionSelectionHistories } from "src/Utils/Hooks/useConstructionSelectionHistories";

interface IProps {
  options?: any;
}

// フィルター選択肢の型定義
interface FilterOption {
  label: string;
  value: string;
}

const WorkScheduleListConstructionSite = (props: IProps) => {
  const auth = useAppSelector(selectAuth);
  const id = auth?.userInfo?.id;
  const { options } = props;
  const dispatch = useDispatch();
  const { sortConstructionSites } = useConstructionSites();
  const [data, setData] = React.useState<any>("");        // テーブル表示用（フィルターで件数が増減する）
  const [allData, setAllData] = React.useState<any>("");  // フィルター選択肢作成用（常に最大件数）
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [loading, setLoading] = React.useState(false);
  // ここからフィルター要コンボボックスの値
  const [constructionFilter, setConstructionFilter] = React.useState("");
  const [leadEngineerFilter, setLeadEngineerFilter] = React.useState("");
  const [responsiblePersonFilter, setResponsiblePersonFilter] = React.useState("");
  const [addressFilter, setAddressFilter] = React.useState("");
  const [primeConstructorFilter, setPrimeConstructorFilter] = React.useState("");
  const [startDateFilter, setStartDateFilter] = React.useState<Date | null>(null);
  const [endDateFilter, setEndDateFilter] = React.useState<Date | null>(null);
  // ここまでフィルター 関数の方は使ってないがおいおい使うかもしれないので取っておく

  const [sortedResponsiblePersonOptions, setSortedResponsiblePersonOptions] = React.useState<{ label: string, value: string }[]>([]);
  const [onlyUnderConstructionFlag, setOnlyUnderConstructionFlag] = React.useState(true);

  const [constructionSiteOptions, setConstructionSiteOptions] = React.useState<any>([]);
  const { list, fetchList, storeSelection } = useUserSelectionHistories(id ?? 0);
  const { constructionList, fetchConstructionList, storeConstructionSelection } = useConstructionSelectionHistories(id ?? 0);

  interface Column {
    id: "no" | "construction" | "lead_engineer" | "responsible_person" | "address" | "prime_constructor" | "start_date" | "end_date";
    label: string;
    minWidth?: number;
    align?: "center";
    fontSize: 14;
    format?: (value: number) => string;
  }

  const columns: readonly Column[] = [
    { id: "no", label: "NO.", minWidth: 60, fontSize: 14 },
    { id: "construction", label: "工事現場名", minWidth: 100, fontSize: 14 },
    {
      id: "lead_engineer",
      label: "主任技術者",
      minWidth: 170,
      align: "center",
      fontSize: 14,
      format: (value: number) => value.toLocaleString("en-US"),
    },
    {
      id: "responsible_person",
      label: "担当",
      minWidth: 170,
      align: "center",
      fontSize: 14,
      format: (value: number) => value.toLocaleString("en-US"),
    },
    {
      id: "address",
      label: "住所",
      minWidth: 170,
      align: "center",
      fontSize: 14,
      format: (value: number) => value.toFixed(2),
    },
    {
      id: "prime_constructor",
      label: "元請け",
      minWidth: 170,
      align: "center",
      fontSize: 14,
      format: (value: number) => value.toFixed(2),
    },
    {
      id: "start_date",
      label: "開始日",
      minWidth: 170,
      align: "center",
      fontSize: 14,
      format: (value: number) => value.toFixed(2),
    },
    {
      id: "end_date",
      label: "終了日",
      minWidth: 170,
      align: "center",
      fontSize: 14,
      format: (value: number) => value.toFixed(2),
    },
  ];

  /*
   * フィルターの選択肢変更時には、他のフィルターをクリアしたうえでデータを取得する。
   */
  // 工事現場名フィルター変更時
  const handleConstructionFilterChange = (selectedOption: FilterOption | null) => {
    setConstructionFilter(selectedOption?.value || "");

    setLeadEngineerFilter("");
    setResponsiblePersonFilter("");
    setAddressFilter("");
    setPrimeConstructorFilter("");
    setStartDateFilter(null);
    setEndDateFilter(null);
    storeConstructionSelection({user_id: id, selected_construction_id: selectedOption?.value });
  };

  //現場名選択肢を履歴順に設定
  React.useEffect(() => {
    setConstructionSiteOptions(constructionList || []);
  },[constructionList]);

  // フィルターのどれか一つでも変わればデータ取得し直し
  React.useEffect(() => {
    fetchListConstructionSite();
  }, [constructionFilter, leadEngineerFilter, responsiblePersonFilter, addressFilter, 
      primeConstructorFilter, startDateFilter, endDateFilter]);

  // 主任技術者フィルター変更時
  const handleLeadEngineerFilterChange = (selectedOption: FilterOption | null) => {
    setLeadEngineerFilter(selectedOption?.value || "");
    
    setConstructionFilter("");
    setResponsiblePersonFilter("");
    setAddressFilter("");
    setPrimeConstructorFilter("");
    setStartDateFilter(null);
    setEndDateFilter(null);
  };

  // 担当フィルター変更時
  const handleResponsiblePersonFilterChange = (selectedOption: FilterOption | null) => {
    setResponsiblePersonFilter(selectedOption?.value || "");

    setConstructionFilter("");
    setLeadEngineerFilter("");
    setAddressFilter("");
    setPrimeConstructorFilter("");
    setStartDateFilter(null);
    setEndDateFilter(null);
    storeSelection({ user_id: id, selected_user_id: selectedOption?.value });
  };

  // 住所フィルター変更時
  const handleAddressFilterChange = (selectedOption: FilterOption | null) => {
    setAddressFilter(selectedOption?.value || "");

    setConstructionFilter("");
    setLeadEngineerFilter("");
    setResponsiblePersonFilter("");
    setPrimeConstructorFilter("");
    setStartDateFilter(null);
    setEndDateFilter(null);
  };

  // 元請けフィルター変更時
  const handlePrimeConstructorFilterChange = (selectedOption: FilterOption | null) => {
    setPrimeConstructorFilter(selectedOption?.value || "");

    setConstructionFilter("");
    setLeadEngineerFilter("");
    setResponsiblePersonFilter("");
    setAddressFilter("");
    setStartDateFilter(null);
    setEndDateFilter(null);
  };

  // 開始日・終了日フィルター変更時
  const handleDateFilterChange = (dateRange: { startDate: Date | null; endDate: Date | null }) => {
    setStartDateFilter(dateRange.startDate);
    setEndDateFilter(dateRange.endDate);

    setConstructionFilter("");
    setLeadEngineerFilter("");
    setResponsiblePersonFilter("");
    setAddressFilter("");
    setPrimeConstructorFilter("");
  };

  const fetchListConstructionSite = async () => {
    setLoading(true);
    try {
      const res = await workScheduleApi.getListConstructionSite({
        construction: constructionFilter,
        leadEngineer: leadEngineerFilter,
        responsiblePerson: responsiblePersonFilter,
        address: addressFilter,
        primeConstructor: primeConstructorFilter,
        startDate: startDateFilter,
        endDate: endDateFilter,
      });

      let tempData = res?.data?.data;
      // 初期表示時（フィルタが設定されていない時）は工事中のデータを一覧表示する（直感に反する動きだが顧客の要望）
      if (!constructionFilter && !leadEngineerFilter && !responsiblePersonFilter && 
          !addressFilter && !primeConstructorFilter && !startDateFilter && !endDateFilter) {
        tempData = tempData.filter((item: any) => {
          // 終了日が今日以降のデータが工事中データ
          return moment(item.end_date).isSameOrAfter(moment(), 'day');
        });
      }
      const sortedData = sortConstructionSites(tempData);
      setData(sortedData);
      // フィルタコンボボックスの項目は常に最大件数のデータで作成するため、そのためのデータを設定しておく
      if (!constructionFilter && !leadEngineerFilter && !responsiblePersonFilter && 
          !addressFilter && !primeConstructorFilter && !startDateFilter && !endDateFilter) {
        // フィルタクリアした際に実際にはデータが変わっていないのにuseMemoの処理が再実行されてしまう。
        // useMemoの依存変数はオブジェクトの場合参照比較のため。パフォーマンスに影響が出たら要対応。
        setAllData(sortedData);
      }
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      if (err.response?.status === 404) {
        setData(null);
        dispatch(
          updateSnackBar({
            messageType: "error",
            message: err.response?.data?.message,
          })
        );
      } else {
        dispatch(
          updateSnackBar({
            messageType: "error",
            message: err.response?.data?.message,
          })
        );
      }
    }
  };

  function createData(no: string, construction?: string, lead_engineer?: string, responsible_person?: string, address?: string, prime_constructor?: string, start_date?: string, end_date?: string) {
    return {
      no,
      construction,
      lead_engineer,
      responsible_person,
      address,
      prime_constructor,
      start_date,
      end_date,
    };
  }

  const rows = useMemo(() => {
    if (!data) {
      return [];
    } else {
      return data?.map((items, index) =>
        createData(
          `${data?.length - index}`,
          `${items?.name ? items?.name : "--"}`,
          `${items?.lead_engineer ? items?.lead_engineer : "--"}`,
          `${items?.in_charge?.first_name && items?.in_charge?.last_name ? items?.in_charge?.last_name + " " + items?.in_charge?.first_name : "--"}`,
          `${items?.address ? items?.address : "--"}`,
          `${items?.note ? items?.note : `--`}`,
          `${items?.start_date ? items?.start_date : "--"}`,
          `${items?.end_date ? items?.end_date : "--"}`
        )
      );
    }
  }, [data]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  /*
   * フィルタの選択肢作成処理
   */
  // 工事現場名の選択肢
  const constructionOptions = useMemo(() => {
    if (!options?.constructions?.length) return [];
    
    /*
     * 工事現場名だけ親コンポーネントから受け取ったデータで作成（元々の実装に従った）
     * それ以外のフィルタは、optionsに含まれていないデータもあるため改めて取得している(allData)
     * 2回fetchが起こるわけなのでoptionsに今回追加されたデータを含めるようにしたほうが良いのだが、
     * バックエンドとの関連でインターフェースに関わる部分なため他の使用箇所に影響がありそうなので断念。
     */
    return options?.constructions.map((item) => {
      return {
        label: item.name,
        value: item.id.toString(),
      };
    });
  }, [options?.constructions]);

  // 主任技術者の選択肢
  const leadEngineerOptions = useMemo(() => {
    if (!allData) return [];
    
    // lead_engineerフィールドを使用して選択肢を生成
    const uniqueEngineers = allData
      .filter(item => item.lead_engineer) // 値が存在するものだけ
      .map(item => item.lead_engineer)
      .filter((value, index, self) => self.indexOf(value) === index); // 重複除去
    // セレクトボックス用のオプション形式に変換
    return uniqueEngineers.map(engineer => ({
      label: engineer,
      value: engineer
    }));
  }, [allData]);

  // 担当者の選択肢
  const responsiblePersonOptions = useMemo(() => {
    if (!allData) return [];
    
    // in_chargeフィールドを使用して選択肢を生成
    const uniqueResponsiblePersons = allData
      .filter(item => item.in_charge?.first_name && item.in_charge?.last_name)
      .map(item => ({
        name: `${item.in_charge.last_name} ${item.in_charge.first_name}`,
        value: item.in_charge.id.toString()
      }))
      .filter((person, index, self) => 
        index === self.findIndex(p => p.value === person.value)
      );
    
    // セレクトボックス用のオプション形式に変換
    return uniqueResponsiblePersons.map(person => ({
      label: person.name,
      value: person.value
    }));
  }, [allData]);

  // 担当者の選択肢をユーザー選択履歴と照合して並べ替え
  React.useEffect(() => {
    if (!responsiblePersonOptions.length) {
      setSortedResponsiblePersonOptions([]);
      return;
    }
    const sortedIds = list.map(user => Number(user.id));
    const sorted = [...responsiblePersonOptions].sort((a, b) => {
      const indexA = sortedIds.indexOf(Number(a.value));
      const indexB = sortedIds.indexOf(Number(b.value));
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
    setSortedResponsiblePersonOptions(sorted);
  
}, [responsiblePersonOptions, list]);

  // 住所の選択肢
  const addressOptions = useMemo(() => {
    if (!allData) return [];
    
    // addressフィールドを使用して選択肢を生成
    const uniqueAddresses = allData
      .filter(item => item.address)
      .map(item => item.address)
      .filter((value, index, self) => self.indexOf(value) === index);
    
    // セレクトボックス用のオプション形式に変換
    return uniqueAddresses.map(address => ({
      label: address,
      value: address
    }));
  }, [allData]);

  // 元請けの選択肢
  const primeConstructorOptions = useMemo(() => {
    if (!allData) return [];
    
    // noteフィールドを使用して選択肢を生成
    const uniquePrimeConstructors = allData
      .filter(item => item.note)
      .map(item => item.note)
      .filter((value, index, self) => self.indexOf(value) === index);
    
    // セレクトボックス用のオプション形式に変換
    return uniquePrimeConstructors.map(constructor => ({
      label: constructor,
      value: constructor
    }));
  }, [allData]);

  return (
    <StyledWorkScheduleListConstructionSite>
      <div className="">
        <Grid item sm={12}>
          <Grid container columnSpacing={1} rowSpacing={0}>
            <Grid item xs={12} lg={4}>
              <div className="input">
                <AppCustomeSelect
                  placeholder={"工事現場名検索"}
                  value={constructionFilter ? { label: constructionSiteOptions.find(c => c.id.toString() === constructionFilter)?.name || "", value: constructionFilter } : null}
                  onChange={handleConstructionFilterChange}
                  className="construction-search"
                  // options={options?.constructions.map((item) => {
                  //   return {
                  //     label: item.name,
                  //     value: item.id.toString(),
                  //   };
                  // })}
                  options={constructionSiteOptions.map((item) => {
                    return {
                      label: item.name,
                      value: item.id.toString(),
                    };
                  })}
                  isClearable
                />
              </div>
            </Grid>

            <Grid item xs={12} lg={3}>
              <div className="input">
                <AppCustomeSelect
                  placeholder={"主任技術者検索"}
                  value={leadEngineerFilter ? { label: leadEngineerFilter, value: leadEngineerFilter } : null}
                  onChange={handleLeadEngineerFilterChange}
                  className="lead_engineer-search"
                  options={leadEngineerOptions}
                  isClearable
                />
              </div>
            </Grid>

            <Grid item xs={12} lg={3}>
              <div className="input">
                <AppCustomeSelect
                  placeholder={"担当検索"}
                  value={responsiblePersonFilter ? responsiblePersonOptions.find(p => p.value === responsiblePersonFilter) || null : null}
                  onChange={handleResponsiblePersonFilterChange}
                  className="responsible_person-search"
                  options={sortedResponsiblePersonOptions}
                  isClearable
                />
              </div>
            </Grid>

            <Grid item xs={12} lg={4}>
              <div className="input">
                <AppCustomeSelect
                  placeholder={"住所検索"}
                  value={addressFilter ? { label: addressFilter, value: addressFilter } : null}
                  onChange={handleAddressFilterChange}
                  className="address-search"
                  options={addressOptions}
                  isClearable
                />
              </div>
            </Grid>

            <Grid item xs={12} lg={3}>
              <div className="input">
                <AppCustomeSelect
                  placeholder={"元請け検索"}
                  value={primeConstructorFilter ? { label: primeConstructorFilter, value: primeConstructorFilter } : null}
                  onChange={handlePrimeConstructorFilterChange}
                  className="prime_constructor-search"
                  options={primeConstructorOptions}
                  isClearable
                />
              </div>
            </Grid>

            <Grid item xs={12} md={12} lg={3}>
              <div className="input">
                <div className="datePicker">
                  <AppSingleFieldRangeDatePicker
                    value={{
                      startDate: startDateFilter,
                      endDate: endDateFilter
                    }}
                    onChange={handleDateFilterChange}
                    placeholder="開始日・終了日"
                  />
                </div>
              </div>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={4}>
            <div className="rowsPerPage">
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={rows?.length}
                rowsPerPage={rowsPerPage}
                labelRowsPerPage="1ページあたりの表示数"
                page={page}
                onPageChange={noop}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={() => ""}
                ActionsComponent={() => null}
                sx={{
                  marginLeft: "-15px",
                  // the root <Toolbar>
                  "& .MuiTablePagination-toolbar": {
                    fontSize: "12px",
                  },
                  // the “Rows per page” label
                  "& .MuiTablePagination-selectLabel": {
                    fontSize: "12px",
                  },
                  // the number dropdown itself
                  "& .MuiSelect-select": {
                    fontSize: "12px",
                    paddingLeft: "5px",
                    paddingRihgt: "5px",
                    paddingTop: "0px",
                    paddingBottom: "0px"
                  },
                  // the “x–y of z” text
                  "& .MuiTablePagination-displayedRows": {
                    fontSize: "12px",
                  },
                }}
              />
            </div>
          </Grid>
          <Grid item xs={12} lg={4}>
            <CircularLoader loading={loading} />
            <Paper className="paperStyle" sx={{ width: "100%", overflow: "hidden" }}>
              <TableContainer sx={{ maxHeight: 600, overflow: "auto" }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            minWidth: column.minWidth,
                            fontSize: column.fontSize,
                          }}
                          className="header"
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows &&
                      rows?.length > 0 &&
                      rows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)?.map((row) => {
                        return (
                          <TableRow hover role="checkbox" tabIndex={-1} key={row.construction}>
                            {columns.map((column) => {
                              const value = row[column.id];
                              return (
                                <TableCell key={column.id} align={column.align} style={{ fontSize: "12px" }}>
                                  {column.format && typeof value === "number" ? column.format(value) : value}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={rows?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={noop}
              sx={{
                // the root <Toolbar>
                "& .MuiTablePagination-toolbar": {
                  fontSize: "12px",
                },
                // the “Rows per page” label
                "& .MuiTablePagination-selectLabel": {
                  fontSize: "12px",
                },
                // the number dropdown itself
                "& .MuiSelect-select": {
                  fontSize: "12px",
                },
                // the “x–y of z” text
                "& .MuiTablePagination-displayedRows": {
                  fontSize: "13px",
                },
                "& .MuiTablePagination-actions button": {
                  padding: "10px",  // clickable area
                  minWidth: 36, // controls the button “box” size
                  "& svg": {
                    fontSize: "22px", // arrows size
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </div>
    </StyledWorkScheduleListConstructionSite>
  );
};

export default WorkScheduleListConstructionSite;
